import { PortalScheduleSlot, PortalSession, createPortalClient } from './portalClient';

export const TEACHER_PORTAL_ENABLED =
  process.env.REACT_APP_TEACHER_PORTAL === 'true' || process.env.NODE_ENV !== 'production';

const ROLE_TEACHER = 4;

const client = createPortalClient({
  storageKey: 'cecap_teacher_session',
  role: ROLE_TEACHER,
  wrongRoleMessage: 'Esta cuenta no es de docente. Usa el portal que corresponde a tu cuenta.',
  photoPath: '/employees/portal/me/photo',
});

export type TeacherSession = PortalSession;
export type GradeStatus = 'PENDING' | 'PASSED' | 'FAILED';

export interface TeacherCourse {
  id: number;
  label: string;
  year: number | null;
  status: 'OPEN' | 'CLOSED';
  subject: { id: number; name: string };
  career: { id: number; name: string; code: string | null };
  module: { id: number; name: string; order: number };
  classroom: string | null;
  location: string | null;
  modality: string;
  schedule: string;
  schedule_slots: PortalScheduleSlot[];
  student_count: number;
  pending_count: number;
}

export interface TeacherPortalData {
  teacher: {
    id: number;
    full_name: string;
    username: string | null;
    email: string | null;
    document_number: string;
    phone: string | null;
    photo: string | null;
  };
  school: { name: string | null; logo: string | null };
  courses: TeacherCourse[];
}

export interface GradeComponent {
  id: number;
  name: string;
  weight: number;
  order: number;
}

export interface CourseStudentRow {
  grade_id: number;
  student: {
    id: number;
    full_name: string;
    student_code: string | null;
    document_number: string;
    email: string | null;
    photo: string | null;
  };
  values: Record<string, number>;
  partial: number | null;
  complete: boolean;
  grade: number | null;
  status: GradeStatus;
  editable: boolean;
}

export interface CourseDetail {
  course: TeacherCourse;
  scale: { min_grade: number; max_grade: number; passing_grade: number };
  components: GradeComponent[];
  weights_total: number;
  students: CourseStudentRow[];
}

export const getTeacherSession = client.getSession;
export const logoutTeacher = client.logout;
export const teacherLogin = client.login;
export const uploadTeacherPhoto = client.uploadPhoto;
export const changeTeacherPassword = client.changePassword;
export const forceChangeTeacherPassword = client.forceChangePassword;

const coursePath = (courseId: number) => `/employees/portal/courses/${courseId}`;

export function fetchTeacherPortal(): Promise<TeacherPortalData> {
  return client.request<TeacherPortalData>('/employees/portal/me', 'No se pudieron cargar tus datos. Intenta más tarde.');
}

export function fetchCourseDetail(courseId: number): Promise<CourseDetail> {
  return client.request<CourseDetail>(coursePath(courseId), 'No se pudo cargar el curso.');
}

export function addGradeComponent(courseId: number, name: string, weight: number): Promise<CourseDetail> {
  return client.request<CourseDetail>(`${coursePath(courseId)}/components`, 'No se pudo agregar el corte.', {
    method: 'POST',
    body: JSON.stringify({ name, weight }),
  });
}

export function updateGradeComponent(
  courseId: number, componentId: number, name: string, weight: number,
): Promise<CourseDetail> {
  return client.request<CourseDetail>(
    `${coursePath(courseId)}/components/${componentId}`,
    'No se pudo actualizar el corte.',
    { method: 'PUT', body: JSON.stringify({ name, weight }) },
  );
}

export function deleteGradeComponent(courseId: number, componentId: number): Promise<CourseDetail> {
  return client.request<CourseDetail>(
    `${coursePath(courseId)}/components/${componentId}`,
    'No se pudo eliminar el corte.',
    { method: 'DELETE' },
  );
}

export type ComponentValues = Record<string, number | null>;

export function saveComponentGrades(
  courseId: number, grades: { grade_id: number; values: ComponentValues }[],
): Promise<CourseDetail> {
  return client.request<CourseDetail>(`${coursePath(courseId)}/grades`, 'No se pudieron guardar las notas.', {
    method: 'PUT',
    body: JSON.stringify({ grades }),
  });
}

export function finalizeCourse(courseId: number, grades?: Record<string, number>): Promise<CourseDetail> {
  return client.request<CourseDetail>(
    `${coursePath(courseId)}/finalize`,
    'No se pudieron cerrar las notas.',
    { method: 'POST', body: JSON.stringify(grades ? { grades } : {}) },
  );
}
